import { useEffect, useRef, useState } from 'react';
import { CAR_MODELS, filterOptions, MOTO_MODELS, VEHICLE_COLORS } from '@mobi/shared';
import { Input, cx } from './ui.js';

/**
 * Campo de texto com sugestão de uma lista fixa.
 *
 * A lista é estática e local — não é busca no servidor, é filtro num
 * array na memória, sem debounce nem rede. Continua sendo texto livre por
 * baixo: quem tem algo fora da lista digita e envia normalmente, só sem
 * sugestão.
 */
export function SuggestInput({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const suggestions = open ? filterOptions(options, value) : [];
  const picking = useRef(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          if (picking.current) {
            picking.current = false;
            return;
          }
          setOpen(false);
        }}
        required
        placeholder={placeholder}
        autoComplete="off"
      />

      {open && suggestions.length > 0 && (
        <ul className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-2xl border border-line bg-surface shadow-2xl">
          {suggestions.map((m) => (
            <li key={m}>
              <button
                type="button"
                onMouseDown={() => {
                  picking.current = true;
                  onChange(m);
                  setOpen(false);
                }}
                className={cx(
                  'block w-full truncate px-4 py-3 text-left text-sm transition hover:bg-surface-2',
                  m === value && 'text-brand',
                )}
              >
                {m}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ModelPicker({
  type,
  value,
  onChange,
}: {
  type: 'car' | 'moto';
  value: string;
  onChange: (model: string) => void;
}) {
  return (
    <SuggestInput
      value={value}
      onChange={onChange}
      options={type === 'car' ? CAR_MODELS : MOTO_MODELS}
      placeholder={type === 'car' ? 'Chevrolet Onix' : 'Honda CG 160'}
    />
  );
}

export function ColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  return <SuggestInput value={value} onChange={onChange} options={VEHICLE_COLORS} placeholder="Prata" />;
}
